(() => {
  let cleanup = [];
  function initializeNavigation() {
    cleanup.forEach(dispose => dispose());
    cleanup = [];
    document.querySelectorAll('[data-site-header]').forEach(header => {
      const toggle = header.querySelector('.menu-toggle');
      const navigation = header.querySelector('nav');
      if (!toggle || !navigation) return;
      const openLabel = toggle.dataset.labelOpen || toggle.getAttribute('aria-label');
      const closeLabel = toggle.dataset.labelClose || openLabel;
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const close = () => {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', openLabel);
        navigation.classList.remove('open');
      };
      close();
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.setAttribute('aria-label', expanded ? closeLabel : openLabel);
        navigation.classList.toggle('open', expanded);
      }, options);
      navigation.addEventListener('click', event => {
        if (event.target.closest('a')) close();
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
          close();
          toggle.focus();
        }
      }, options);
      document.addEventListener('click', event => {
        if (!header.contains(event.target)) close();
      }, options);
      const localLinks = [...navigation.querySelectorAll('a')].filter(link => {
        const url = new URL(link.href, location.href);
        return url.origin === location.origin && url.pathname === location.pathname && url.hash.length > 1;
      });
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          localLinks.forEach(link => link.classList.toggle('active', decodeURIComponent(link.hash.slice(1)) === entry.target.id));
        }
      }, { rootMargin: '-10% 0px -65% 0px', threshold: 0 });
      localLinks.forEach(link => {
        const section = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (section) observer.observe(section);
      });
      cleanup.push(() => { controller.abort(); observer.disconnect(); });
    });
  }
  initializeNavigation();
  // Shopify replaces section markup without reloading the page in its editor.
  document.addEventListener('shopify:section:load', initializeNavigation);
  document.addEventListener('shopify:section:reorder', initializeNavigation);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(initializeNavigation));
})();

// Custom elements also reconnect when Shopify replaces a section in its editor.
for (const carouselName of ['horzo-portfolio-carousel', 'horzo-review-carousel']) {
 if (!customElements.get(carouselName)) {
  customElements.define(carouselName, class extends HTMLElement {
    connectedCallback() {
      this.dispose();
      const track = this.querySelector('[data-carousel-track]');
      const previous = this.querySelector('[data-carousel-prev]');
      const next = this.querySelector('[data-carousel-next]');
      if (!track || !previous || !next) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const update = () => {
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        const position = Math.abs(track.scrollLeft);
        previous.hidden = next.hidden = maximum < 2;
        previous.disabled = position < 2;
        next.disabled = position >= maximum - 2;
      };
      const move = direction => {
        const slide = track.querySelector('[data-carousel-slide], [data-portfolio-slide]');
        if (!slide) return;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const sign = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        track.scrollBy({ left: direction * sign * (slide.getBoundingClientRect().width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      previous.addEventListener('click', () => move(-1), options);
      next.addEventListener('click', () => move(1), options);
      track.addEventListener('scroll', update, { ...options, passive: true });
      this.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('[data-carousel-slide], [data-portfolio-slide]');
        if (slide && this.contains(slide)) slide.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'auto' });
      }, options);
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(track);
      } else {
        window.addEventListener('resize', update, options);
      }
      update();
    }
    disconnectedCallback() { this.dispose(); }
    dispose() {
      if (this.controller) this.controller.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  });
}

}

// Native details remain usable without JavaScript or native dialog support.
(() => {
  if (customElements.get('horzo-project-card')) return;
  const openDialogs = new Set();
  let savedOverflow;
  function lockScroll(dialog) {
    if (openDialogs.size === 0) savedOverflow = { value: document.documentElement.style.getPropertyValue('overflow'), priority: document.documentElement.style.getPropertyPriority('overflow') };
    openDialogs.add(dialog);
    document.documentElement.style.setProperty('overflow', 'hidden');
  }
  function unlockScroll(dialog) {
    if (!openDialogs.delete(dialog) || openDialogs.size !== 0) return;
    if (savedOverflow.value) document.documentElement.style.setProperty('overflow', savedOverflow.value, savedOverflow.priority);
    else document.documentElement.style.removeProperty('overflow');
  }
  customElements.define('horzo-project-card', class extends HTMLElement {
    connectedCallback() {
      this.dispose();
      const details = this.querySelector('details');
      const summary = details?.querySelector('summary');
      const dialog = this.querySelector('[data-project-dialog]');
      const content = details?.querySelector('[data-project-content]');
      const close = dialog?.querySelector('[data-project-close]');
      if (!details || !summary || !content || !close || typeof dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      this.details = details;
      this.dialog = dialog;
      this.content = content;
      this.summary = summary;
      const options = { signal: this.controller.signal };
      dialog.append(content);
      details.open = false;
      summary.setAttribute('aria-haspopup', 'dialog');
      summary.setAttribute('aria-controls', dialog.id);
      summary.addEventListener('click', event => {
        event.preventDefault();
        if (dialog.open || !dialog.isConnected) return;
        dialog.showModal();
        lockScroll(dialog);
        close.focus({ preventScroll: true });
      }, options);
      close.addEventListener('click', () => dialog.close(), options);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      }, options);
      dialog.addEventListener('close', () => {
        if (dialog.open) return;
        unlockScroll(dialog);
        if (summary.isConnected && !document.querySelector('[data-project-dialog][open]')) summary.focus({ preventScroll: true });
      }, options);
    }
    disconnectedCallback() { this.dispose(); }
    dispose() {
      if (this.controller) this.controller.abort();
      if (this.dialog) {
        if (this.dialog.open) this.dialog.close();
        unlockScroll(this.dialog);
      }
      if (this.content && this.details) this.details.append(this.content);
      if (this.summary) {
        this.summary.removeAttribute('aria-haspopup');
        this.summary.removeAttribute('aria-controls');
      }
      this.dialog = this.content = this.details = this.summary = null;
    }
  });
})();


// Reveal counters once on entry; the original values stay available without JS.
if (!customElements.get('horzo-statistics')) {
  customElements.define('horzo-statistics', class extends HTMLElement {
    connectedCallback() {
      this.stop();
      this.values = [...this.querySelectorAll('[data-count-target]')].map(element => {
        const original = element.dataset.countTarget;
        const match = original.match(/^([^0-9]*)([0-9]+(?:[.,][0-9]{1,2})?)([^0-9]*)$/u);
        const output = element.querySelector('[data-counter-value]');
        if (!match || !output) return null;
        const separator = match[2].includes(',') ? ',' : '.';
        const decimals = match[2].includes(separator) ? match[2].split(separator)[1].length : 0;
        return { output, original, prefix: match[1], suffix: match[3], target: Number(match[2].replace(',', '.')), separator, decimals };
      }).filter(value => value && Number.isFinite(value.target));
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      if (this.played || this.dataset.animate !== 'true' || this.motion.matches || !('IntersectionObserver' in window)) return;
      this.controller = new AbortController();
      this.motion.addEventListener('change', event => { if (event.matches) { this.played = true; this.stop(); } }, { signal: this.controller.signal });
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.played = true;
        const duration = Math.max(600, Math.min(2400, Number(this.dataset.duration) || 1400));
        let start;
        const tick = now => {
          start ??= now;
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          this.values.forEach(value => {
            const number = value.decimals ? (value.target * eased).toFixed(value.decimals) : String(Math.floor(value.target * eased));
            value.output.textContent = progress === 1 ? value.original : value.prefix + number.replace('.', value.separator) + value.suffix;
          });
          if (progress < 1) this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
      }, { threshold: 0.2 });
      this.observer.observe(this.querySelector('.stats'));
    }
    disconnectedCallback() { this.stop(); }
    stop() {
      if (this.frame !== undefined) cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.controller?.abort();
      this.values?.forEach(value => { value.output.textContent = value.original; });
    }
  });
}
